import { townColorHex as hex } from './townPropTemplates';
import type { TownPropPalette, TownLanternTemplate } from './townPropTemplates';

/** Original post SVG, retained byte-for-byte when no named template is selected. */
export function lanternPostBody(glow: number): string {
  const g = hex(glow);
  return `
    <defs>
      <radialGradient id="lg" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="${g}" stop-opacity="0.95" />
        <stop offset="0.45" stop-color="${g}" stop-opacity="0.5" />
        <stop offset="1" stop-color="${g}" stop-opacity="0" />
      </radialGradient>
    </defs>
    <circle cx="28" cy="23" r="24" fill="url(#lg)" />
    <rect x="25" y="34" width="6" height="72" fill="#4a3020" />
    <path d="M25 34 v72" stroke="#2a1608" stroke-width="1.2" opacity="0.5" />
    <rect x="18" y="104" width="20" height="8" rx="2" fill="#4a3020" />
    <rect x="22" y="15" width="12" height="17" fill="${g}" opacity="0.92" />
    <path d="M19 12 h18 l-2 22 h-14 Z" fill="none" stroke="#2a2a2a" stroke-width="2.5" />
    <path d="M28 32 v-20" stroke="#2a2a2a" stroke-width="1.4" opacity="0.7" />
    <rect x="21" y="8" width="14" height="5" rx="2" fill="#2a2a2a" />
    <circle cx="28" cy="6" r="2.2" fill="#2a2a2a" />`;
}

/** Distinct reusable lamp structures, baked once into the existing 56 × 112 sprite. */
export function buildTownLanternBody(template: TownLanternTemplate, p: TownPropPalette): string {
  const iron = hex(p.iron), wood = hex(p.wood), edge = hex(p.woodEdge);
  const glow = hex(p.primary), core = hex(p.glassCore);
  const lamp = (cx: number, cy: number, w: number, h: number) => `
    <circle cx="${cx}" cy="${cy}" r="${Math.min(cx, 56 - cx, 19)}" fill="url(#town_lamp_glow)" />
    <path d="M${cx - w / 2 - 2} ${cy - h / 2}l3 -5h${w - 2}l3 5Z" fill="${iron}" />
    <rect x="${cx - w / 2}" y="${cy - h / 2}" width="${w}" height="${h}" fill="${glow}" />
    <rect x="${cx - w * 0.2}" y="${cy - h * 0.3}" width="${w * 0.4}" height="${h * 0.6}" fill="${core}" opacity="0.65" />
    <path d="M${cx - w / 2} ${cy - h / 2}v${h}h${w}v-${h}Z M${cx} ${cy - h / 2}v${h}" fill="none" stroke="${iron}" stroke-width="2" />
    <path d="M${cx - w / 2 - 1} ${cy + h / 2 + 2}h${w + 2}" stroke="${edge}" stroke-width="2" />`;
  const defs = `<defs><radialGradient id="town_lamp_glow"><stop stop-color="${glow}" stop-opacity="0.55" /><stop offset="1" stop-color="${glow}" stop-opacity="0" /></radialGradient></defs>`;
  if (template === 'crook_lantern') return `${defs}
    <path d="M17 108V35q0 -20 14 -20q13 0 13 14" fill="none" stroke="${iron}" stroke-width="5" />
    <path d="M19 103V39" stroke="${edge}" stroke-width="1.3" />
    <path d="M12 106h12v6H12Z M14 65h7v5h-7Z" fill="${iron}" />
    ${lamp(41, 42, 13, 20)}`;
  if (template === 'twin_lantern') return `${defs}
    <path d="M25 34h6v73h-6Z" fill="${wood}" />
    <path d="M27 39v65" stroke="${edge}" stroke-width="1.5" />
    <path d="M8 28h40 M12 28v9 M44 28v9 M28 19v15" stroke="${iron}" stroke-width="4" />
    <path d="M19 106h18v6H19Z M23 77h10v5H23Z" fill="${iron}" />
    ${lamp(12, 46, 11, 18)} ${lamp(44, 46, 11, 18)}`;
  return `${defs}
    <path d="M24 43h8v65h-8Z" fill="${wood}" />
    <path d="M26 49v56" stroke="${edge}" stroke-width="2" />
    <path d="M18 108l7 -16h6l7 16Z" fill="${wood}" />
    <path d="M16 106h24v6H16Z M23 68h10v6H23Z M23 92h10v5H23Z" fill="${iron}" />
    <path d="M28 11v9" stroke="${iron}" stroke-width="3" />
    ${lamp(28, 33, 19, 27)}
    <path d="M19 28h18 M19 40h18" stroke="${iron}" stroke-width="1.5" />`;
}

/** 22×44 ceiling fixture. Its top-center chain is the attachment point; no standing post or glow halo. */
export function buildHangingLanternBody(p: TownPropPalette): string {
  const iron = hex(p.iron), edge = hex(p.woodEdge), glass = hex(p.primary), core = hex(p.glassCore);
  return `
    <g data-part="hanging-chain">
      <path d="M11 0V3 M11 12V17" stroke="${iron}" stroke-width="1.5" />
      <ellipse cx="11" cy="4" rx="1.5" ry="2.7" fill="none" stroke="${iron}" stroke-width="1.1" />
      <ellipse cx="11" cy="8.5" rx="1" ry="2.7" fill="none" stroke="${edge}" stroke-width="0.9" />
      <ellipse cx="11" cy="13" rx="1.5" ry="2.7" fill="none" stroke="${iron}" stroke-width="1.1" />
    </g>
    <g data-part="hanging-cage">
      <path d="M3 21L7 17H15L19 21Z" fill="${iron}" />
      <path d="M5 21H17L16 37H6Z" fill="${glass}" />
      <path d="M9 23H13V34H9Z" fill="${core}" opacity="0.75" />
      <path d="M4 21H18L17 38H5Z M11 21V38" fill="none" stroke="${iron}" stroke-width="1.5" />
      <path d="M5 27H17 M5 34H17" stroke="${iron}" stroke-width="0.8" />
      <path d="M4 39H18L15 42H7Z M10 42H12V44H10Z" fill="${iron}" />
      <path d="M7 19H15 M6 40H16" stroke="${edge}" stroke-width="0.7" />
    </g>`;
}
